import React, { useState, useEffect, useCallback } from 'react'
import { Activity, CheckCircle, XCircle, Clock, RefreshCw, AlertTriangle, Loader2 } from 'lucide-react'
import { api } from '../lib/api'

interface WorkerStats {
  workers: Record<string, any>
  total_workers: number
  active_tasks: Record<string, any>
  registered_tasks: Record<string, any>
  error?: string
}

interface Task {
  task_id: string
  media_id: number
  title: string
  filename: string
  status: string
  created_at: string
  error_message?: string
  celery_status?: string
  celery_result?: string
}

interface MonitoringStats {
  media_by_status: Record<string, number>
  redis_connected: boolean
  queue_length: number
  redis_memory: string
}

export default function MonitoringPage() {
  const [workers, setWorkers] = useState<WorkerStats | null>(null)
  const [tasks, setTasks] = useState<Task[]>([])
  const [stats, setStats] = useState<MonitoringStats | null>(null)
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [loading, setLoading] = useState(true)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [retrying, setRetrying] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    try {
      const [workersRes, tasksRes, statsRes] = await Promise.all([
        api.get('/monitoring/workers'),
        api.get('/monitoring/tasks/recent', {
          params: {
            limit: 100,
            ...(selectedStatus !== 'all' && { status: selectedStatus })
          }
        }),
        api.get('/monitoring/stats')
      ])

      setWorkers(workersRes.data)
      setTasks(tasksRes.data)
      setStats(statsRes.data)
    } catch (error: any) {
      console.error('Error fetching monitoring data:', error)
    } finally {
      setLoading(false)
    }
  }, [selectedStatus])

  useEffect(() => {
    fetchData()
    
    if (autoRefresh) {
      const interval = setInterval(fetchData, 5000) // Refresh every 5s
      return () => clearInterval(interval)
    }
  }, [autoRefresh, fetchData])

  const retryTask = async (taskId: string) => {
    try {
      setRetrying(taskId)
      await api.post(`/monitoring/tasks/${taskId}/retry`)
      await fetchData()
    } catch (error: any) {
      console.error('Error retrying task:', error)
      alert(error?.response?.data?.detail || 'Erreur lors du retry')
    } finally {
      setRetrying(null)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'indexed':
      case 'SUCCESS':
        return <CheckCircle className="h-5 w-5 text-emerald-500" />
      case 'error':
      case 'FAILURE':
        return <XCircle className="h-5 w-5 text-red-500" />
      case 'transcribing':
      case 'indexing':
      case 'uploaded':
      case 'PENDING':
      case 'STARTED':
        return <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
      default:
        return <AlertTriangle className="h-5 w-5 text-zinc-500" />
    }
  }

  const getStatusBadge = (status: string) => {
    const baseClasses = 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold'
    
    switch (status) {
      case 'indexed':
        return `${baseClasses} bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200`
      case 'error':
        return `${baseClasses} bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200`
      case 'transcribing':
        return `${baseClasses} bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200`
      case 'indexing':
        return `${baseClasses} bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200`
      case 'uploaded':
        return `${baseClasses} bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200`
      default:
        return `${baseClasses} bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300`
    }
  }

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    )
  }

  return (
    <div className="relative overflow-hidden">
      <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-40 h-[320px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.25),_transparent_68%)]" />

      <div className="relative mx-auto max-w-[1600px] px-6 py-6">
        <div className="mb-4">
          <button
            onClick={() => window.location.href = '/admin'}
            className="inline-flex items-center gap-2 rounded-full border border-zinc-200/70 dark:border-zinc-800 bg-white/80 dark:bg-zinc-900/70 px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 shadow-sm transition hover:bg-zinc-50 dark:hover:bg-zinc-800"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            Retour
          </button>
        </div>
        <div className="mb-6 flex items-center justify-end gap-4">
          <label className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="h-4 w-4 rounded border-zinc-300 text-emerald-600 focus:ring-emerald-500 dark:border-zinc-700"
            />
            <span>Auto-refresh (5s)</span>
          </label>
          <button
            onClick={fetchData}
            className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600"
          >
            <RefreshCw className="h-4 w-4" />
            Actualiser
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="rounded-2xl border border-emerald-100/70 bg-white/90 p-6 shadow-lg dark:border-emerald-900/50 dark:bg-zinc-950/90">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-300">
            Workers Actifs
          </h3>
          <p className="mt-2 text-3xl font-bold text-zinc-900 dark:text-white">
            {workers?.total_workers || 0}
          </p>
          {workers?.error && (
            <p className="mt-1 text-xs text-red-500">{workers.error}</p>
          )}
        </div>

        <div className="rounded-2xl border border-blue-100/70 bg-white/90 p-6 shadow-lg dark:border-blue-900/50 dark:bg-zinc-950/90">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-300">
            File d'attente
          </h3>
          <p className="mt-2 text-3xl font-bold text-zinc-900 dark:text-white">
            {stats?.queue_length || 0}
          </p>
        </div>

        <div className="rounded-2xl border border-red-100/70 bg-white/90 p-6 shadow-lg dark:border-red-900/50 dark:bg-zinc-950/90">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-red-600 dark:text-red-300">
            Tâches Échouées
          </h3>
          <p className="mt-2 text-3xl font-bold text-red-600 dark:text-red-400">
            {stats?.media_by_status?.error || 0}
          </p>
        </div>

        <div className="rounded-2xl border border-purple-100/70 bg-white/90 p-6 shadow-lg dark:border-purple-900/50 dark:bg-zinc-950/90">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-purple-600 dark:text-purple-300">
            Redis
          </h3>
          <p className="mt-2 text-sm font-bold">
            {stats?.redis_connected ? (
              <span className="text-emerald-600 dark:text-emerald-400">
                ✓ Connecté ({stats.redis_memory})
              </span>
            ) : (
              <span className="text-red-600 dark:text-red-400">✗ Déconnecté</span>
            )}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4">
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:ring-emerald-500 dark:border-zinc-700 dark:bg-zinc-800 dark:text-white"
        >
          <option value="all">Tous les statuts</option>
          <option value="uploaded">En attente</option>
          <option value="transcribing">Transcription</option>
          <option value="indexing">Indexation</option>
          <option value="indexed">Terminé</option>
          <option value="error">Échoué</option>
        </select>
      </div>

      {/* Tasks Table */}
      <div className="overflow-hidden rounded-2xl border border-zinc-200/70 bg-white shadow-lg dark:border-zinc-800 dark:bg-zinc-900">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-zinc-200 dark:divide-zinc-800">
            <thead className="bg-zinc-50 dark:bg-zinc-950">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                  Fichier
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                  Task ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
              {tasks.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    Aucune tâche trouvée
                  </td>
                </tr>
              ) : (
                tasks.map((task) => (
                  <tr
                    key={task.task_id}
                    className="hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
                  >
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(task.status)}
                        <span className={getStatusBadge(task.status)}>
                          {task.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="max-w-xs">
                        <div className="truncate text-sm font-medium text-zinc-900 dark:text-white">
                          {task.title || task.filename}
                        </div>
                        {task.error_message && (
                          <div className="mt-1 text-xs text-red-500 dark:text-red-400">
                            {task.error_message}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <code className="rounded bg-zinc-100 px-2 py-1 text-xs text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                        {task.task_id?.substring(0, 8)}...
                      </code>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-zinc-600 dark:text-zinc-400">
                      {new Date(task.created_at).toLocaleString('fr-FR')}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      {task.status === 'error' && (
                        <button
                          onClick={() => retryTask(task.task_id)}
                          disabled={retrying === task.task_id}
                          className="flex items-center gap-1 text-sm font-medium text-emerald-600 hover:text-emerald-700 disabled:opacity-50 dark:text-emerald-400 dark:hover:text-emerald-300"
                        >
                          {retrying === task.task_id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4" />
                          )}
                          Réessayer
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
